import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Plus, Calculator as CalculatorIcon } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { useReveal } from "../../hooks/useReveal";

const fmt = (n) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CostCalculator = ({ c }) => {
  const [amount, setAmount] = useState("100");
  const sale = Math.max(0, parseFloat(amount) || 0);

  const commission = sale * 0.04;
  const fixed = sale > 0 ? 0.5 : 0;
  const totalCost = commission + fixed;
  const itbms = totalCost * 0.07;
  const net = Math.max(0, sale - totalCost - itbms);

  return (
    <div
      data-testid="cost-calculator"
      className="mt-6 rounded-3xl border border-black/[0.08] bg-white shadow-[0_10px_40px_rgba(0,0,0,0.05)] overflow-hidden"
    >
      <div className="grid lg:grid-cols-2">
        {/* Input side */}
        <div className="p-8 lg:p-10 bg-[#fafcf9]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-black flex items-center justify-center">
              <CalculatorIcon size={20} className="text-[#0fe814]" />
            </div>
            <h3 className="font-bold text-[20px] text-black">{c.title}</h3>
          </div>
          <p className="mt-3 text-[14px] text-black/55 font-medium">{c.subtitle}</p>

          <label className="block mt-8 text-[12px] font-bold uppercase tracking-wider text-black/45">
            {c.inputLabel}
          </label>
          <div className="mt-2 relative max-w-xs">
            <span className="absolute left-5 top-1/2 -translate-y-1/2 text-[22px] font-extrabold text-black/40">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="calculator-input"
              className="w-full pl-11 pr-5 py-4 text-[26px] font-extrabold text-black bg-white border-2 border-black/10 rounded-2xl focus:border-[#0fb013] focus:outline-none transition-colors duration-200"
            />
          </div>

          <div className="mt-8 space-y-3">
            <div className="flex items-center justify-between text-[13px] font-medium text-black/60">
              <span>{c.base}</span>
              <span className="font-bold text-black">${fmt(sale)}</span>
            </div>
            <div className="flex items-center justify-between text-[13px] font-medium text-black/60">
              <span>{c.commission}</span>
              <span className="font-bold text-[#ff603e]">-${fmt(commission)}</span>
            </div>
            <div className="flex items-center justify-between text-[13px] font-medium text-black/60">
              <span>{c.fixedCost}</span>
              <span className="font-bold text-[#ff603e]">-${fmt(fixed)}</span>
            </div>
            <div className="flex items-center justify-between text-[13px] font-medium text-black/60 border-t border-black/[0.07] pt-3">
              <span>{c.totalCost}</span>
              <span className="font-bold text-black">-${fmt(totalCost)}</span>
            </div>
            <div className="flex items-center justify-between text-[13px] font-medium text-black/60">
              <span>{c.itbms}</span>
              <span className="font-bold text-[#ff603e]">-${fmt(itbms)}</span>
            </div>
          </div>
        </div>

        {/* Net result side */}
        <div className="relative p-8 lg:p-10 bg-black text-white flex flex-col justify-center overflow-hidden">
          <div className="absolute bottom-[-120px] left-[-100px] w-[300px] h-[300px] rounded-full bg-[#0fe814]/15 blur-3xl pointer-events-none" />
          <span className="relative text-[12px] font-bold uppercase tracking-wider text-white">{c.net}</span>
          <div
            data-testid="calculator-net"
            className="relative mt-3 font-extrabold text-[56px] lg:text-[68px] leading-none text-[#0fe814]"
          >
            ${fmt(net)}
          </div>
          <p className="relative mt-3 text-[13px] font-medium text-white">{c.netHint}</p>
          <p className="relative mt-8 pt-6 border-t border-white/10 text-[11px] font-medium text-white max-w-sm">
            {c.disclaimer}
          </p>
        </div>
      </div>
    </div>
  );
};

const Pricing = () => {
  const { t } = useLanguage();
  const [ref, visible] = useReveal(0.1);
  const p = t.pricing;

  return (
    <section id="precios" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <h2 className="font-extrabold text-[28px] sm:text-[36px] lg:text-[42px] tracking-tight text-black">
          {p.title}
        </h2>
        <p className="mt-4 text-[16px] text-black/55 font-medium max-w-2xl">{p.subtitle}</p>

        <div
          ref={ref}
          className={`mt-12 grid lg:grid-cols-5 gap-6 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Rate card */}
          <div
            data-testid="pricing-rate-card"
            className="lg:col-span-2 relative overflow-hidden rounded-3xl bg-black text-white p-8 lg:p-10 flex flex-col"
          >
            <div className="absolute top-[-120px] right-[-120px] w-[320px] h-[320px] rounded-full bg-[#0fe814]/15 blur-3xl pointer-events-none" />
            <span className="relative inline-block self-start text-[11px] font-bold uppercase tracking-wider text-black bg-[#0fe814] px-3 py-1.5 rounded-full">
              {p.rateLabel}
            </span>

            <div className="relative mt-8 flex items-end flex-wrap gap-x-4 gap-y-2">
              <div>
                <div className="font-extrabold text-[64px] lg:text-[76px] leading-none text-[#0fe814]">
                  {p.commission}
                </div>
                <div className="mt-2 text-[12px] font-semibold text-white">{p.commissionLabel}</div>
              </div>
              <div className="pb-7 text-white">
                <Plus size={26} strokeWidth={3} />
              </div>
              <div>
                <div className="font-extrabold text-[64px] lg:text-[76px] leading-none text-white">
                  {p.fixed}
                </div>
                <div className="mt-2 text-[12px] font-semibold text-white">{p.fixedLabel}</div>
              </div>
            </div>
            <p className="relative mt-4 text-[13px] font-medium text-white">{p.perTx}</p>

            {/* breakdown */}
            <div className="relative mt-8 space-y-3 flex-1">
              {p.breakdown.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between border-b border-white/10 pb-3"
                >
                  <span className="text-[13px] font-medium text-white">{row.label}</span>
                  <span className="text-[15px] font-extrabold text-white">{row.value}</span>
                </div>
              ))}
            </div>

            <Link
              to="/registro"
              data-testid="pricing-cta"
              className="relative mt-8 inline-flex items-center justify-center gap-2 text-[14px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-8 py-4 rounded-full transition-all duration-200 shadow-[0_6px_20px_rgba(15,232,20,0.35)]"
            >
              {p.cta}
              <ArrowRight size={17} />
            </Link>
          </div>

          {/* Included list */}
          <div className="lg:col-span-3 rounded-3xl border border-black/[0.08] bg-[#fafcf9] p-8 lg:p-10">
            <h3 className="font-bold text-[20px] lg:text-[22px] text-black">{p.includedTitle}</h3>
            <ul className="mt-7 grid sm:grid-cols-2 gap-x-8 gap-y-5">
              {p.included.map((item, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`pricing-included-${i}`}>
                  <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-[#0fe814]/20 flex items-center justify-center">
                    <Check size={13} className="text-[#0fb013]" strokeWidth={3} />
                  </span>
                  <span className="text-[14px] leading-relaxed text-black/70 font-medium">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 pt-6 border-t border-black/[0.07] text-[12px] font-medium text-black/40">
              {p.note}
            </p>
          </div>
        </div>

        {/* Cost calculator */}
        <CostCalculator c={p.calculator} />
      </div>
    </section>
  );
};

export default Pricing;
